import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import { useDispatch, useSelector } from 'react-redux'
import { endOfMonth, startOfMonth, subMonths } from 'date-fns'
import { toast } from 'react-toastify'
import FilterCalendar from '../../calendar/FilterCalendar'
import DeleteButton from '../../buttons/actions-button/DeleteButton'
import EditButton from '../../buttons/actions-button/EditButton'
import {
  Cargo,
  ClientShipmentCargo,
  fetchClientShipments,
  setCargoModalReportTabConstraints,
  setCurrentCargo,
  setCurrentCargoGroupIdClient,
  setIsDisabledEdit,
  setIsOnlyCargo,
  setIsVisibleDeleteModalCargo,
  setIsVisibleModalCargo,
} from '../../../redux/reducers/cargos'
import {
  fetchCurrentReport,
  setCurrentReportId,
  setIsVisibleModalReport,
} from '../../../redux/reducers/reports'
import { cargosSelector } from '../../../redux/selectors/cargos-selector'
import { reportsSelector } from '../../../redux/selectors/reports-selector'
import { AppDispatch } from '../../../redux/store'
import { formatDateToDDMMYYYY } from '../../../utils/date'
import { getAmount, getItemCurrency } from '../../../utils/getAmount'
import { getRole } from '../../../utils/getRole'
import { canCurrentUserDeleteCargo } from '../../../utils/cargoDeletePermission'
import { getReportStatusLabel } from '../../../utils/reportStatus'
import {
  formatCargoReadinessListDate,
  parseCargoIsPaid,
  parseCargoReadyForPickup,
} from '../report-modal/cargos/cargoReadinessDate'
import axiosInstance from '../../../utils/axiosInstance'
import { URL } from '../../../constants/url'
import { default as Select } from 'react-select'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import cargoStyles from '../cargo-side-modal/CargoSideModal.module.css'
import reportStyles from '../report-modal/ReportModal.module.css'
import accountsStyles from '../../../pages/documents/accounts/Accounts.module.css'
import styles from './AddClientModal.module.css'

const getDefaultDateRange = () => {
  const now = new Date()
  return {
    startDate: startOfMonth(subMonths(now, 1)),
    endDate: endOfMonth(now),
  }
}

const partyName = (party?: { entity_name?: string; name?: string } | null) =>
  party?.entity_name || party?.name || '—'

type ClientOption = { value: number; label: string }

const ALL_CLIENTS_OPTION: ClientOption = { value: 0, label: 'Все' }

type RoleOption = { value: '' | 'from' | 'to'; label: string }

const ROLE_OPTIONS: RoleOption[] = [
  { value: '', label: 'Не важно' },
  { value: 'from', label: 'Отправитель' },
  { value: 'to', label: 'Получатель' },
]

const clientSelectStyles = {
  control: (base: Record<string, unknown>, { isFocused }: { isFocused: boolean }) => ({
    ...base,
    minHeight: '38px',
    height: '38px',
    boxShadow: 'inset 1px 3px 5px 0px #0000001c',
    backgroundColor: '#fff',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: 400,
    color: '#012140',
    transition: 'all 0.3s ease',
    border: 'none',
    outline: 'none',
    '&:hover': { border: 'none', outline: 'none' },
    '&:focus': {
      border: 'none',
      outline: 'none',
      boxShadow: isFocused
        ? 'inset 1px 3px 5px 0px #0000001c'
        : 'inset 1px 3px 5px 0px #0000001c',
    },
  }),
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    height: '40px',
    padding: '0 15px',
  }),
  input: (base: Record<string, unknown>) => ({
    ...base,
    margin: '0px',
    padding: '0px',
    color: '#012140',
    '& input': {
      height: '38px',
      boxShadow: 'none !important',
      border: 'none !important',
    },
  }),
  indicatorsContainer: (base: Record<string, unknown>) => ({
    ...base,
    height: '38px',
  }),
  option: (
    base: Record<string, unknown>,
    { isFocused, isSelected }: { isFocused: boolean; isSelected: boolean }
  ) => ({
    ...base,
    backgroundColor: isSelected ? '#0a4475' : 'white',
    color: isSelected ? 'white' : isFocused ? '#b60e27' : '#012140',
    padding: '8px 15px',
    fontSize: '16px',
    borderBottom: '1px solid #e0e0e0',
    '&:active': { backgroundColor: '#0a4475', color: 'white' },
    '&:hover': { color: '#b60e27', backgroundColor: 'white' },
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    marginTop: '5px',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
    borderRadius: '5px',
    overflow: 'hidden',
  }),
  menuPortal: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 10001,
  }),
  singleValue: (base: Record<string, unknown>) => ({
    ...base,
    color: '#012140',
  }),
  placeholder: (base: Record<string, unknown>) => ({
    ...base,
    color: '#69697a',
  }),
}

const ClientSelectDropdownIndicator = (props: {
  innerProps: React.HTMLAttributes<HTMLDivElement>
  selectProps: { menuIsOpen: boolean }
}) => (
  <div {...props.innerProps} style={{ padding: '4px', marginRight: '10px' }}>
    <svg
      width="16"
      height="16"
      viewBox="0 0 14 4"
      fill="currentColor"
      style={{
        transition: 'transform 0.2s ease, margin-bottom 0.2s ease',
        transform: props.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0)',
        marginBottom: props.selectProps.menuIsOpen ? '-4px' : '0',
      }}
    >
      <path d="M0.636,0.293C0.824,0.105,1.078,0,1.343,0S1.862,0.105,2.05,0.293L7,5.243L11.95,0.293c0.189-0.182,0.441-0.283,0.703-0.281c0.262,0.002,0.513,0.107,0.698,0.293c0.391,0.391,0.391,1.025,0,1.416l-5.657,5.657c-0.188,0.188-0.442,0.293-0.707,0.293s-0.519-0.105-0.707-0.293L0.636,1.709C0.247,1.318,0.247,0.684,0.636,0.293z" />
    </svg>
  </div>
)

const reportNumberClass = (status?: string) => {
  if (status === 'in_trip') {
    return classNames(cargoStyles.bgGreen, cargoStyles.colorWhite)
  }
  if (status === 'draft') return accountsStyles.bgRed
  return classNames(styles.bgDarkBlue, cargoStyles.colorWhite)
}

const reportStatusClass = (status?: string) => {
  if (status === 'in_trip') return accountsStyles.colorGreen
  if (status === 'draft') return cargoStyles.colorRed
  return styles.colorBlack
}

const ClientShipmentsTab: React.FC<{ clientId: number }> = ({ clientId }) => {
  const dispatch = useDispatch<AppDispatch>()
  const { isClient } = getRole()
  const { isVisibleModalCargo, isVisibleDeleteModalCargo } =
    useSelector(cargosSelector)
  const { isVisibleModalReport } = useSelector(reportsSelector)

  const defaultRange = useMemo(() => getDefaultDateRange(), [])
  const [startDate, setStartDate] = useState<Date>(defaultRange.startDate)
  const [endDate, setEndDate] = useState<Date>(defaultRange.endDate)
  const [asPayer, setAsPayer] = useState(false)
  const [role, setRole] = useState<'' | 'from' | 'to'>('')
  const [counterparty, setCounterparty] = useState('')
  const [items, setItems] = useState<ClientShipmentCargo[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const cargoModalWasOpen = useRef(false)
  const deleteModalWasOpen = useRef(false)
  const reportModalWasOpen = useRef(false)

  const counterpartyOptions = useMemo(() => {
    if (role !== 'from' && role !== 'to') return []
    const map = new Map<number, string>()
    items.forEach((item) => {
      const party = role === 'from' ? item.to : item.from
      const id = role === 'from' ? item.to_id : item.from_id
      if (!id || !party) return
      if (!map.has(id)) map.set(id, party.entity_name || party.name || '—')
    })
    return Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) =>
      a.label.localeCompare(b.label, 'ru')
    )
  }, [items, role])

  const visibleItems = useMemo(() => {
    if (!counterparty) return items
    const id = Number(counterparty)
    if (role === 'from') return items.filter((item) => item.to_id === id)
    if (role === 'to') return items.filter((item) => item.from_id === id)
    return items
  }, [counterparty, items, role])

  const load = useCallback(() => {
    setIsLoading(true)
    const params: {
      client: number
      date_from: string
      date_to: string
      as_payer: number
      role?: 'from' | 'to'
    } = {
      client: clientId,
      date_from: startDate.toLocaleDateString('en-CA'),
      date_to: endDate.toLocaleDateString('en-CA'),
      as_payer: asPayer ? 1 : 0,
    }
    if (role) params.role = role
    dispatch(fetchClientShipments(params))
      .unwrap()
      .then((data) => {
        const list = Array.isArray(data)
          ? data
          : Array.isArray((data as { data?: ClientShipmentCargo[] })?.data)
            ? (data as { data: ClientShipmentCargo[] }).data
            : []
        setItems(list)
      })
      .catch(() => setItems([]))
      .finally(() => setIsLoading(false))
  }, [asPayer, clientId, dispatch, endDate, role, startDate])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!counterparty || isLoading) return
    const exists = counterpartyOptions.some(
      (item) => String(item.value) === counterparty
    )
    if (!exists) setCounterparty('')
  }, [counterparty, counterpartyOptions, isLoading])

  useEffect(() => {
    if (cargoModalWasOpen.current && !isVisibleModalCargo) load()
    cargoModalWasOpen.current = isVisibleModalCargo
  }, [isVisibleModalCargo, load])

  useEffect(() => {
    if (deleteModalWasOpen.current && !isVisibleDeleteModalCargo) load()
    deleteModalWasOpen.current = isVisibleDeleteModalCargo
  }, [isVisibleDeleteModalCargo, load])

  useEffect(() => {
    if (reportModalWasOpen.current && !isVisibleModalReport) load()
    reportModalWasOpen.current = isVisibleModalReport
  }, [isVisibleModalReport, load])

  const groups = useMemo(() => {
    const map = new Map<number, ClientShipmentCargo[]>()
    visibleItems.forEach((item) => {
      const key = item.report?.id ?? item.report_id
      const list = map.get(key) || []
      list.push(item)
      map.set(key, list)
    })
    return Array.from(map.values())
  }, [visibleItems])

  const counterpartyLine = (cargo: ClientShipmentCargo) => {
    if (cargo.from_id === clientId) return `⊳  ${partyName(cargo.to)}`
    if (cargo.to_id === clientId) return `${partyName(cargo.from)}  ⊳`
    return `${partyName(cargo.from)}  ⊳  ${partyName(cargo.to)}`
  }

  const handleOpenReport = async (id: number) => {
    try {
      await dispatch(fetchCurrentReport(id)).unwrap()
      dispatch(setCurrentReportId(id))
      dispatch(setIsVisibleModalReport(true))
    } catch {
      toast.error('Не удалось открыть рейс')
    }
  }

  const handleEdit = async (item: ClientShipmentCargo) => {
    try {
      const response = await axiosInstance.get<Cargo & { direction?: string }>(
        `${URL.cargos}/${item.id}`
      )
      const cargo = response.data
      const direction =
        cargo.direction === 'forward' || cargo.direction === 'backward'
          ? cargo.direction
          : undefined
      const listKind =
        cargo.to_id === clientId && cargo.from_id !== clientId
          ? 'unloading'
          : 'loading'
      dispatch(setIsDisabledEdit(false))
      dispatch(
        setCargoModalReportTabConstraints({
          lockPl: true,
          lockSender: false,
          ...(direction ? { direction } : {}),
          listKind,
        })
      )
      dispatch(setCurrentCargoGroupIdClient(cargo.from_id ?? null))
      dispatch(setCurrentCargo(cargo))
      dispatch(setIsOnlyCargo(true))
      dispatch(setIsVisibleModalCargo(true))
    } catch {
      toast.error('Не удалось открыть груз')
    }
  }

  const handleDelete = (item: ClientShipmentCargo) => {
    dispatch(setIsOnlyCargo(true))
    dispatch(setCurrentCargo(item as never))
    dispatch(setIsVisibleDeleteModalCargo(true))
  }

  return (
    <div>
      <div className={classNames(styles.filterForm, 'mb-3')}>
        <div className="row align-items-end">
          <div className="order-1 col-12 col-sm-6 pb-2">
            <FilterCalendar
              startDate={startDate}
              endDate={endDate}
              isFixed={false}
              horizontalOnDesktop
              onDateChange={(start, end) => {
                setStartDate(start)
                setEndDate(end)
              }}
            />
          </div>
          <div className="order-4 order-sm-2 col-12 col-sm-6 pb-2 align-self-center">
            <div className={cargoStyles.small3}>
              <input
                type="checkbox"
                id="client_shipments_payer"
                checked={asPayer}
                onChange={(e) => setAsPayer(e.target.checked)}
              />
              <label htmlFor="client_shipments_payer">Плательщик</label>
            </div>
          </div>
          <div className="order-2 order-sm-3 col-6 col-sm-6 pb-2">
            <small className={classNames(cargoStyles.small2, 'd-block mb-1')}>
              Роль
            </small>
            <Select<RoleOption>
              options={ROLE_OPTIONS}
              value={ROLE_OPTIONS.find((item) => item.value === role) || ROLE_OPTIONS[0]}
              onChange={(option) => {
                const next = option?.value || ''
                setRole(next)
                if (!next) setCounterparty('')
              }}
              placeholder="Роль"
              isSearchable={false}
              styles={clientSelectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              components={{
                IndicatorSeparator: () => null,
                DropdownIndicator: ClientSelectDropdownIndicator,
              }}
            />
          </div>
          <div className="order-3 order-sm-4 col-6 col-sm-6 pb-2">
            {(role === 'from' || role === 'to') && (
              <>
                <small className={classNames(cargoStyles.small2, 'd-block mb-1')}>
                  {role === 'from' ? 'Кому отправил' : 'Кто отправил'}
                </small>
                <Select<ClientOption>
                  options={[ALL_CLIENTS_OPTION, ...counterpartyOptions]}
                  value={
                    counterpartyOptions.find(
                      (item) => String(item.value) === counterparty
                    ) || ALL_CLIENTS_OPTION
                  }
                  onChange={(option) =>
                    setCounterparty(option?.value ? String(option.value) : '')
                  }
                  placeholder={role === 'from' ? 'Кому отправил' : 'Кто отправил'}
                  noOptionsMessage={() => 'Клиент не найден'}
                  isSearchable
                  styles={clientSelectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  components={{
                    IndicatorSeparator: () => null,
                    DropdownIndicator: ClientSelectDropdownIndicator,
                  }}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {isLoading && (
        <div className={classNames(styles.small2, 'pt-2 pb-3')} style={{ textAlign: 'center' }}>
          <span className={styles.colorGray}>Загрузка...</span>
        </div>
      )}

      {!isLoading && !groups.length && (
        <div className={classNames(styles.small2, 'pt-2 pb-3')} style={{ textAlign: 'center' }}>
          <span className={styles.colorGray}>Нет грузов</span>
        </div>
      )}

      {groups.map((cargos) => {
        const report = cargos[0]?.report
        if (!report) return null
        return (
          <div
            key={report.id}
            className={classNames(
              cargoStyles.bgGray,
              cargoStyles.bxShadowSmall,
              cargoStyles.border10,
              cargoStyles.small2,
              'pt-1 pb-1 pl-2 pr-2 mb-3'
            )}
          >
            <div className={classNames(styles.rowMin, 'row')}>
              <a
                className={classNames(
                  styles.shipmentReportLink,
                  cargoStyles.small3,
                  'col-auto col-sm-3 pr-2 pt-1 pb-1'
                )}
                onClick={() => handleOpenReport(report.id)}
              >
                <span
                  className={classNames(
                    'd-inline-block pt-0 pb-0 pl-2 pr-2 mb-1',
                    cargoStyles.borderRadius5,
                    reportNumberClass(report.status)
                  )}
                >
                  <b>{report.number || 'б/н'}</b>
                </span>
                <br />
                <span
                  className={classNames(
                    'd-inline-block',
                    reportStatusClass(report.status)
                  )}
                >
                  {getReportStatusLabel(report.status)}
                </span>
              </a>
              <div
                className={classNames(
                  cargoStyles.small2,
                  'col col-sm-5 pr-2 pt-1 pb-1'
                )}
              >
                <div>
                  <b>{report.user?.name || '—'}</b>
                </div>
                {report.car?.name}
              </div>
              <div
                className={classNames(
                  cargoStyles.small3,
                  'col-auto col-sm-4 pt-1 pb-1'
                )}
                style={{ textAlign: 'right' }}
              >
                {report.departure ? formatDateToDDMMYYYY(report.departure) : '—'}
                &nbsp;⊳
                <br />
                ⊲&nbsp;
                {report.arrival ? formatDateToDDMMYYYY(report.arrival) : '—'}
              </div>
            </div>

            <div>
            {cargos.map((item) => {
              const isReady = parseCargoReadyForPickup(item.can_be_picked_up)
              const readyDate = isReady ? '' : formatCargoReadinessListDate(item.readiness_at)
              return (
                <div
                  key={item.id}
                  className={classNames(
                    cargoStyles.bgWhite,
                    cargoStyles.bxShadow,
                    cargoStyles.border10,
                    cargoStyles.small2,
                    'pt-1 pb-1 mb-1'
                  )}
                >
                  <div className="row align-items-center ml-1 mr-1">
                    <div className="col-12 col-sm pl-1">
                      <div className={classNames(cargoStyles.ellipsis2, 'mb-1')}>
                        {item.description}
                      </div>
                      <div className={classNames(cargoStyles.small3, 'mb-1')}>
                        {counterpartyLine(item)}
                      </div>
                      <div className={cargoStyles.small3}>
                        <b>
                          {getAmount(item.amount ?? 0)}
                          &nbsp;
                          {getItemCurrency(item.currency)}
                        </b>
                        &nbsp;{' '}
                        {item.payment_method === 'cash'
                          ? 'Нал'
                          : item.payment_method === 'bank_transfer'
                            ? 'Безнал'
                            : null}
                        {parseCargoIsPaid(item.is_paid) && (
                          <i
                            className={classNames(
                              cargoStyles.icon,
                              cargoStyles.iconLike,
                              reportStyles.filterGreen,
                              reportStyles.icon20,
                              'ml-1 mb-1'
                            )}
                            title="Оплачено!"
                          />
                        )}
                      </div>
                    </div>
                    <div className="col-12 col-sm-auto pl-1 pr-0" style={{ textAlign: 'right' }}>
                      {isReady && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip
                              id={`cargo-ready-${item.id}`}
                              style={{ zIndex: 10050 }}
                            >
                              Можно забирать
                            </Tooltip>
                          }
                        >
                          <span
                            className={classNames(
                              cargoStyles.small3,
                              accountsStyles.colorTurquoise,
                              'd-inline-block ml-1 mr-2'
                            )}
                          >
                            <i
                              className={classNames(
                                cargoStyles.icon,
                                cargoStyles.iconVerified2,
                                reportStyles.filterTurq,
                                cargoStyles.icon16
                              )}
                            />{' '}
                            <b>Готов</b>
                          </span>
                        </OverlayTrigger>
                      )}
                      {!isReady && readyDate && (
                        <span
                          className={classNames(
                            cargoStyles.small3,
                            cargoStyles.colorBlue,
                            'd-inline-block ml-2 mr-2'
                          )}
                          title="Будет готово"
                        >
                          {readyDate}
                        </span>
                      )}
                      {item.images && item.images.length > 0 && (
                        <PhotoProvider className={classNames(cargoStyles.photoProvider, 'd-inline-block')}>
                          {item.images.map((photo, index) => (
                            <PhotoView
                              key={photo.id}
                              src={photo?.image?.lg || (photo?.image as { original?: string })?.original}
                            >
                              {index === 0 ? (
                                <span
                                  className={classNames(
                                    cargoStyles.btn,
                                    cargoStyles.btnSmall2,
                                    cargoStyles.btnBlueOutline,
                                    cargoStyles.btnIcon,
                                    'd-inline-block'
                                  )}
                                >
                                  <i className={classNames(cargoStyles.icon, cargoStyles.iconPicture)} />
                                </span>
                              ) : (
                                <img style={{ display: 'none' }} alt="" />
                              )}
                            </PhotoView>
                          ))}
                        </PhotoProvider>
                      )}
                      <EditButton onClick={() => handleEdit(item)} />
                      {!isClient &&
                        canCurrentUserDeleteCargo({
                          created_by: item.created_by,
                          report: item.report?.user
                            ? { user: { id: item.report.user.id } }
                            : undefined,
                        }) && (
                        <DeleteButton onClick={() => handleDelete(item)} />
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default ClientShipmentsTab
